/**
 * Scrub bar (04 Transport): full race 0 → t_end on a small device-resolution Canvas2D.
 *
 * - Lap ticks every lap (leader lap starts), labels every 5 laps.
 * - Event pips coloured by type (story overtakes, pit stops, leader changes, fastest laps,
 *   retirement, penalties, flags). Pips keep their stage-px size; a wider stage gives more px per
 *   lap (09 §8).
 * - Hover shows a tooltip with the event text (nearest pip within 6 stage px) or the time. On
 *   touch (plan T3) the tooltip also shows on press, and a 500 ms press without movement links
 *   the pip under it (the same `onPipHover` path the mouse hover uses); both clear on release.
 *   A touch move never links pips, drag or not (review TF1) — only the hold does.
 * - Dragging seeks continuously: `onScrub` every move (cheap clock seek), `onSeek` throttled to
 *   30 Hz with a trailing call (style cache rebuilds).
 */
import type { RaceEvent, RGB } from '../core/api.ts';
import type { NeutralPeriod } from '../core/state/neutral.ts';
import type { PixelFonts } from '../core/hud/font.ts';
import { textMask } from '../core/hud/font.ts';
import { tierRun } from '../core/hud/type.ts';
import { suspensionLabel, type Suspension } from '../core/time/suspension.ts';
import type { ChromeScale } from './pixel-label.ts';

export type PipKind = 'overtake' | 'pit' | 'leader' | 'fastest' | 'retire' | 'penalty' | 'flag' | 'start';

export interface Pip {
  t: number;
  kind: PipKind;
  text: string;
  id: string;
}

/** Pip colours (shared chrome palette; styles may override per theme). */
export const PIP_RGB: Readonly<Record<PipKind, RGB>> = {
  overtake: [232, 230, 223],
  pit: [80, 170, 255],
  leader: [255, 214, 120],
  fastest: [190, 110, 255],
  retire: [255, 70, 70],
  penalty: [255, 150, 60],
  flag: [240, 240, 240],
  start: [120, 230, 140],
};

/** Neutralisation range colours: amber for VSC/SC, red for a red flag. */
export const NEUTRAL_RGB: Readonly<Record<NeutralPeriod['kind'], RGB>> = {
  vsc: [255, 176, 32],
  sc: [255, 176, 32],
  red: [255, 64, 64],
};

/** Range tooltip title: 'VSC L8–L9' (laps from the manifest), else just the kind. */
export function neutralTitle(p: NeutralPeriod): string {
  const kind = p.kind.toUpperCase();
  if (p.lap0 === undefined || p.lap1 === undefined) return kind;
  if (p.lap0 === p.lap1) return `${kind} L${String(p.lap0)}`;
  return `${kind} L${String(p.lap0)}–L${String(p.lap1)}`;
}

interface LooseEvent {
  type: string;
  t: number;
  id: string;
  text?: string;
  story?: boolean;
  category?: string;
}

/** The scrub-bar / jump event set from the bundle events (04: story overtakes, pits, leaders, fastest laps, DNF, flags). */
export function pipsFromEvents(events: readonly RaceEvent[]): Pip[] {
  const out: Pip[] = [];
  let chequered = false;
  for (const e of events) {
    const ev = e as unknown as LooseEvent;
    let kind: PipKind | null = null;
    switch (ev.type) {
      case 'overtake':
        kind = ev.story ? 'overtake' : null;
        break;
      case 'pit_stop':
        kind = 'pit';
        break;
      case 'leader_change':
        kind = 'leader';
        break;
      case 'fastest_lap':
        kind = 'fastest';
        break;
      case 'retirement':
        kind = 'retire';
        break;
      case 'race_control':
        kind = ev.category === 'penalty' ? 'penalty' : null;
        break;
      case 'chequered_flag':
        if (!chequered) kind = 'flag';
        chequered = true;
        break;
      case 'start':
        kind = 'start';
        break;
      default:
        kind = null;
    }
    if (kind) out.push({ t: ev.t, kind, text: ev.text ?? ev.type.toUpperCase(), id: ev.id });
  }
  out.sort((a, b) => a.t - b.t);
  return out;
}

export interface ScrubBarOptions {
  duration: number;
  lapStarts: readonly number[];
  pips: readonly Pip[];
  /** neutralisation periods (M8): shaded ranges over the track, tooltip 'VSC L8–L9' */
  neutralPeriods?: readonly NeutralPeriod[];
  /** red-flag suspensions (reconstructed races): a short red band at the stop, tooltip 'RED FLAG · 13:46–15:50' */
  suspensions?: readonly Suspension[];
  /** called on every drag move (immediate clock seek) */
  onScrub(t: number): void;
  /** throttled to 30 Hz with a trailing call (style.onSeek) */
  onSeek(t: number): void;
  onDragState?(dragging: boolean): void;
  formatTime(t: number): string;
  /** pip hover (event linking, M3): the hovered pip's event id, null when none */
  onPipHover?(id: string | null): void;
}

const PIP_W = 2; // stage px
const PIP_H = 8;
/** lap-number labels are tier S */
const LABEL_TIER = 'S';
const SEEK_HZ = 30;
/** T3: a touch press must hold this long (without moving) before the pip under it links. */
export const LONG_PRESS_MS = 500;
/** T3: movement past this (stage px) since the press cancels the long-press link. */
const LONG_PRESS_SLOP_STAGE_PX = 2;

export class ScrubBar {
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly tip: HTMLDivElement;
  private ctx: CanvasRenderingContext2D;
  private sc: ChromeScale = { k: 1, dpr: 1 };
  private fonts: PixelFonts | null = null;
  private t = 0;
  private drawnT = Number.NaN;
  private dirty = true;
  private dragging = false;
  /** the pointer that owns the drag (audit fix 8): move/up/cancel for any other id are ignored */
  private dragId = Number.NaN;
  private lastSeekWall = -Infinity;
  private trailing: ReturnType<typeof setTimeout> | null = null;
  /** T3: the long-press timer and the press state it watches (touch only). */
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private pressX = Number.NaN;
  private pressMoved = false;
  private pendingSeek = Number.NaN;
  private wDev = 1;
  private hDev = 1;
  private ro: ResizeObserver;
  private ariaSec = -1;
  private static labelCache = new Map<string, HTMLCanvasElement>();

  constructor(private readonly o: ScrubBarOptions) {
    this.el = document.createElement('div');
    this.el.id = 'scrub';
    this.el.className = 'scrub';
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'scrub-canvas';
    this.canvas.tabIndex = 0;
    this.canvas.setAttribute('role', 'slider');
    this.canvas.setAttribute('aria-label', 'Race time');
    this.canvas.setAttribute('aria-valuemin', '0');
    this.canvas.setAttribute('aria-valuemax', String(Math.round(o.duration)));
    this.canvas.addEventListener('keydown', this.onKey);
    this.el.appendChild(this.canvas);
    this.tip = document.createElement('div');
    this.tip.className = 'scrub-tip';
    this.tip.hidden = true;
    this.el.appendChild(this.tip);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('scrub: no 2d context');
    this.ctx = ctx;
    this.canvas.addEventListener('pointerdown', this.onDown);
    this.canvas.addEventListener('pointermove', this.onMove);
    this.canvas.addEventListener('pointerup', this.onUp);
    this.canvas.addEventListener('pointercancel', this.onUp);
    this.canvas.addEventListener('lostpointercapture', this.onUp);
    this.canvas.addEventListener('pointerleave', () => {
      this.setPipHover(null);
      if (!this.dragging) this.tip.hidden = true;
    });
    this.ro = new ResizeObserver(() => {
      this.measure();
    });
    this.ro.observe(this.canvas);
  }

  get isDragging(): boolean {
    return this.dragging;
  }

  setScale(sc: ChromeScale): void {
    this.sc = sc;
    ScrubBar.labelCache.clear();
    this.measure();
  }

  setFonts(f: PixelFonts | null): void {
    this.fonts = f;
    ScrubBar.labelCache.clear();
    this.dirty = true;
  }

  /** Update the playhead (call per frame; draws only when it moved a device px). */
  setTime(t: number): void {
    this.t = t;
    const sec = Math.floor(t);
    if (sec !== this.ariaSec) {
      this.ariaSec = sec;
      this.canvas.setAttribute('aria-valuenow', String(sec));
      this.canvas.setAttribute('aria-valuetext', this.o.formatTime(t));
    }
    const x = this.xOf(t);
    if (this.dirty || Math.abs(x - this.xOf(this.drawnT)) >= 0.5 || Number.isNaN(this.drawnT)) this.draw();
  }

  private measure(): void {
    const r = this.canvas.getBoundingClientRect();
    const dpr = this.sc.dpr;
    const w = Math.max(1, Math.round(r.width * dpr));
    const h = Math.max(1, Math.round(r.height * dpr));
    if (w !== this.wDev || h !== this.hDev) {
      this.wDev = w;
      this.hDev = h;
      this.canvas.width = w;
      this.canvas.height = h;
    }
    this.dirty = true;
    this.draw();
  }

  /** stage-px unit in device px */
  private get k(): number {
    return this.sc.k;
  }

  private get padX(): number {
    return Math.round(6 * this.k);
  }

  private xOf(t: number): number {
    const p = this.padX;
    const w = this.wDev - 2 * p;
    return p + (Math.min(Math.max(t, 0), this.o.duration) / this.o.duration) * w;
  }

  private tOf(xDev: number): number {
    const p = this.padX;
    const w = Math.max(1, this.wDev - 2 * p);
    return Math.min(Math.max(((xDev - p) / w) * this.o.duration, 0), this.o.duration);
  }

  private draw(): void {
    this.dirty = false;
    this.drawnT = this.t;
    const c = this.ctx;
    const k = this.k;
    const W = this.wDev;
    const H = this.hDev;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.imageSmoothingEnabled = false;
    c.clearRect(0, 0, W, H);
    const midY = Math.round(H * 0.42);
    const barH = Math.max(1, Math.round(4 * k));
    const x0 = this.padX;
    const x1 = W - this.padX;
    // track
    c.fillStyle = 'rgba(255,255,255,0.16)';
    c.fillRect(x0, midY - (barH >> 1), x1 - x0, barH);
    // progress
    const xp = Math.round(this.xOf(this.t));
    c.fillStyle = 'rgba(255,255,255,0.55)';
    c.fillRect(x0, midY - (barH >> 1), xp - x0, barH);
    // neutralisation ranges (VSC/SC amber, red flag red): shaded bands over the track, under the pips
    const spans = this.o.neutralPeriods;
    if (spans && spans.length > 0) {
      const h = barH + Math.max(1, Math.round(2 * k));
      const y = midY - (h >> 1);
      for (const p of spans) {
        const xa = Math.round(this.xOf(p.t0));
        const xb = Math.round(this.xOf(Math.min(p.t1, this.o.duration)));
        if (xb - xa < 1) continue;
        const rgb = NEUTRAL_RGB[p.kind];
        c.fillStyle = `rgba(${String(rgb[0])},${String(rgb[1])},${String(rgb[2])},0.4)`;
        c.fillRect(xa, y, xb - xa, h);
      }
    }
    // red-flag suspension: a zero-length race-time instant, drawn as a short red band
    for (const s of this.o.suspensions ?? []) {
      const [xa, w] = this.suspensionBand(s);
      c.fillStyle = `rgba(${NEUTRAL_RGB.red.join(',')},0.85)`;
      c.fillRect(xa, midY - barH, w, barH * 2);
    }
    // lap ticks + labels every 5 laps
    const tickW = Math.max(1, Math.round(k));
    const laps = this.o.lapStarts;
    for (let i = 1; i < laps.length; i++) {
      const lt = laps[i] ?? 0;
      const x = Math.round(this.xOf(lt));
      const lapNo = i + 1;
      const major = lapNo % 5 === 0;
      c.fillStyle = major ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.25)';
      const th = Math.round((major ? 7 : 4) * k);
      c.fillRect(x, midY + barH, tickW, th);
      if (major) this.drawLabel(String(lapNo), x, midY + barH + th + Math.round(2 * k));
    }
    // pips
    const pw = Math.max(1, Math.round(PIP_W * k));
    const ph = Math.round(PIP_H * k);
    for (const p of this.o.pips) {
      const x = Math.round(this.xOf(p.t)) - (pw >> 1);
      const rgb = PIP_RGB[p.kind];
      c.fillStyle = `rgb(${String(rgb[0])},${String(rgb[1])},${String(rgb[2])})`;
      c.fillRect(x, midY - barH - ph + Math.round(1 * k), pw, ph);
    }
    // playhead
    const hw = Math.max(2, Math.round(3 * k));
    c.fillStyle = '#FFFFFF';
    c.fillRect(xp - (hw >> 1), Math.round(2 * k), hw, H - Math.round(4 * k));
  }

  private drawLabel(s: string, xCentre: number, y: number): void {
    if (!this.fonts) return;
    const run = tierRun(LABEL_TIER, this.k);
    const key = `${s}|${String(run.n)}`;
    let cv = ScrubBar.labelCache.get(key);
    if (!cv) {
      const m = textMask(this.fonts[run.font], s, 1, run.n);
      cv = document.createElement('canvas');
      cv.width = Math.max(1, m.w);
      cv.height = Math.max(1, m.h);
      const cx = cv.getContext('2d');
      if (cx && m.w > 0) {
        const img = cx.createImageData(m.w, m.h);
        for (let i = 0; i < m.data.length; i++) {
          if (m.data[i] === 1) {
            img.data[i * 4] = 200;
            img.data[i * 4 + 1] = 200;
            img.data[i * 4 + 2] = 200;
            img.data[i * 4 + 3] = 255;
          }
        }
        cx.putImageData(img, 0, 0);
      }
      ScrubBar.labelCache.set(key, cv);
    }
    if (y + cv.height > this.hDev) return;
    this.ctx.drawImage(cv, Math.round(xCentre - cv.width / 2), y);
  }

  // ───────────────────────────── input ─────────────────────────────

  private devX(ev: PointerEvent): number {
    const r = this.canvas.getBoundingClientRect();
    return ((ev.clientX - r.left) / Math.max(1, r.width)) * this.wDev;
  }

  private readonly onDown = (ev: PointerEvent): void => {
    if (ev.button !== 0 || this.dragging) return; // the first pointer to press owns the drag (audit fix 8)
    this.dragging = true;
    this.dragId = ev.pointerId;
    this.canvas.setPointerCapture(ev.pointerId);
    this.o.onDragState?.(true);
    const x = this.devX(ev);
    if (ev.pointerType === 'touch') {
      // T3 hover fallbacks: the tooltip shows on press (a touch has no hover to show it); the
      // pip link waits for a 500 ms no-movement hold, so a quick tap-seek does not flash it
      this.pressX = x;
      this.pressMoved = false;
      this.hover(x, false);
      if (this.holdTimer) clearTimeout(this.holdTimer);
      this.holdTimer = setTimeout(() => {
        this.holdTimer = null;
        if (this.dragging && !this.pressMoved) this.hover(this.pressX, true);
      }, LONG_PRESS_MS);
    }
    this.scrubTo(this.tOf(x));
    ev.preventDefault();
  };

  private readonly onMove = (ev: PointerEvent): void => {
    if (this.dragging && ev.pointerId !== this.dragId) return; // a second finger never scrubs (audit fix 8)
    const x = this.devX(ev);
    if (this.dragging) {
      if (Math.abs(x - this.pressX) > LONG_PRESS_SLOP_STAGE_PX * this.sc.k) this.pressMoved = true;
      this.scrubTo(this.tOf(x));
    }
    // TF1: a touch move never links pips — a seek drag would flash every pip it crosses, and
    // finger jitter would link one within ~16 ms instead of after the 500 ms hold. Only the hold
    // path links. The tooltip still follows the finger; mouse hover and mouse drags link as before.
    this.hover(x, ev.pointerType !== 'touch');
  };

  private readonly onUp = (ev: PointerEvent): void => {
    if (!this.dragging || ev.pointerId !== this.dragId) return; // only the owner ends the drag (audit fix 8)
    this.dragging = false;
    this.dragId = Number.NaN;
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
    if (this.canvas.hasPointerCapture(ev.pointerId)) this.canvas.releasePointerCapture(ev.pointerId);
    this.flushSeek();
    this.o.onDragState?.(false);
    // T3: a touch has no hover to keep the tooltip or the pip link alive: both clear on release
    if (ev.pointerType === 'touch') {
      this.setPipHover(null);
      this.tip.hidden = true;
    }
  };

  /** Slider keys: ←/→ ±10 s, PageUp/PageDown ±60 s, Home/End (handled here, not by the global shortcuts). */
  private readonly onKey = (ev: KeyboardEvent): void => {
    const step: Record<string, number> = { ArrowLeft: -10, ArrowRight: 10, ArrowDown: -10, ArrowUp: 10, PageDown: -60, PageUp: 60 };
    let t: number | null = null;
    const d = step[ev.key];
    if (d !== undefined) t = this.t + d;
    else if (ev.key === 'Home') t = 0;
    else if (ev.key === 'End') t = this.o.duration;
    if (t === null) return;
    ev.preventDefault();
    ev.stopPropagation();
    const tt = Math.min(Math.max(t, 0), this.o.duration);
    this.o.onScrub(tt);
    this.o.onSeek(tt);
  };

  private scrubTo(t: number): void {
    this.o.onScrub(t);
    this.pendingSeek = t;
    const now = performance.now();
    if (now - this.lastSeekWall >= 1000 / SEEK_HZ) {
      this.flushSeek();
    } else if (!this.trailing) {
      this.trailing = setTimeout(() => {
        this.trailing = null;
        this.flushSeek();
      }, 1000 / SEEK_HZ - (now - this.lastSeekWall));
    }
  }

  private flushSeek(): void {
    if (this.trailing) {
      clearTimeout(this.trailing);
      this.trailing = null;
    }
    if (Number.isNaN(this.pendingSeek)) return;
    const t = this.pendingSeek;
    this.pendingSeek = Number.NaN;
    this.lastSeekWall = performance.now();
    this.o.onSeek(t);
  }

  private hover(xDev: number, linkPips = true): void {
    const k = this.k;
    // a suspension band within 2 stage px wins over pips (it is only 2-3 px wide; pips still win elsewhere)
    const sus = (this.o.suspensions ?? []).find((s) => {
      const [xa, w] = this.suspensionBand(s);
      return xDev >= xa - 2 * this.sc.k && xDev <= xa + w + 2 * this.sc.k;
    });
    let best: Pip | null = null;
    let bestD = 6 * k;
    for (const p of sus ? [] : this.o.pips) {
      const d = Math.abs(this.xOf(p.t) - xDev);
      if (d <= bestD) {
        bestD = d;
        best = p;
      }
    }
    // no pip near: a neutralisation range under the cursor names itself (e.g. 'VSC L8–L9')
    let range: NeutralPeriod | null = null;
    if (!best && !sus) {
      const t = this.tOf(xDev);
      for (const p of this.o.neutralPeriods ?? []) {
        if (t >= p.t0 && t < p.t1) {
          range = p;
          break;
        }
      }
    }
    this.setPipHover(best && linkPips ? best.id : null);
    const t = best ? best.t : this.tOf(xDev);
    this.tip.textContent = best
      ? `${this.o.formatTime(t)}  ${best.text}`
      : sus
        ? `${this.o.formatTime(t)}  ${suspensionLabel(sus)}`
        : range
          ? `${this.o.formatTime(t)}  ${neutralTitle(range)}`
          : this.o.formatTime(t);
    this.tip.dataset.kind = best ? best.kind : sus ? 'suspension' : range ? 'neutral' : '';
    this.tip.hidden = false;
    const cssX = xDev / this.sc.dpr;
    this.tip.style.left = `${String(cssX)}px`;
  }

  /** device-px [x, width] of a suspension's red band (at least 3 stage px wide) */
  private suspensionBand(s: Suspension): [number, number] {
    const w = Math.max(1, Math.round(3 * this.sc.k));
    return [Math.round(this.xOf(s.t)) - (w >> 1), w];
  }

  private pipHover: string | null = null;
  private setPipHover(id: string | null): void {
    if (id === this.pipHover) return;
    this.pipHover = id;
    this.o.onPipHover?.(id);
  }

  dispose(): void {
    this.ro.disconnect();
    if (this.trailing) clearTimeout(this.trailing);
    if (this.holdTimer) clearTimeout(this.holdTimer);
  }
}
